// Install: npm install qrcode.react
// src/components/SurveyQRCode.jsx

import { Check, Copy, Download, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';

const SurveyQRCode = ({ surveyUrl, surveyTitle, size = 256 }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(surveyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadQR = () => {
    // Get the SVG element
    const svg = document.getElementById('survey-qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const qrImg = new Image();
    const logoImg = new Image();

    // Use high resolution for download (2048x2048 for TV displays)
    const downloadSize = 2048;
    const scaleFactor = downloadSize / size;

    canvas.width = downloadSize;
    canvas.height = downloadSize;

    // Track which images have loaded
    let qrLoaded = false;
    let logoLoaded = false;

    const drawCanvas = () => {
      if (!qrLoaded || !logoLoaded) return;

      // Disable smoothing for sharp QR code edges (better scanning)
      ctx.imageSmoothingEnabled = false;

      // Draw QR code at high resolution with crisp edges
      ctx.drawImage(qrImg, 0, 0, downloadSize, downloadSize);

      // Enable smoothing for logo only (smoother appearance)
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw logo in the center, scaled proportionally
      const logoSize = 45 * scaleFactor;
      const logoX = (downloadSize - logoSize) / 2;
      const logoY = (downloadSize - logoSize) / 2;
      ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `NovaSage_QR_Code_${surveyTitle.replace(/\s+/g, '_')}.png`;
        link.click();
        URL.revokeObjectURL(url);
      });
    };

    qrImg.onload = () => {
      qrLoaded = true;
      drawCanvas();
    };

    logoImg.onload = () => {
      logoLoaded = true;
      drawCanvas();
    };

    logoImg.onerror = () => {
      // If logo fails to load, proceed without it
      logoLoaded = true;
      drawCanvas();
    };

    // Load QR code SVG
    qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));

    // Load logo
    logoImg.src = '/logo.svg';
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: surveyTitle,
          text: `Take this survey: ${surveyTitle}`,
          url: surveyUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback to copy
      handleCopyUrl();
    }
  };

  return (
    <div className="bg-gradient-card border border-nova-green/30 rounded-lg p-6 max-w-sm">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-white mb-1">Survey QR Code</h3>
        <p className="text-sm text-gray-400">{surveyTitle}</p>
      </div>

      {/* QR Code */}
      <div className="flex justify-center mb-4 p-4 bg-white border-2 border-nova-green/20 rounded-lg">
        <QRCodeSVG
          id="survey-qr-code"
          value={surveyUrl}
          size={size}
          level="H"
          includeMargin={true}
          imageSettings={{
            src: "/logo.svg",
            x: undefined,
            y: undefined,
            height: 45,
            width: 45,
            excavate: true,
          }}
        />
      </div>

      {/* Survey URL */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-300 mb-1">Survey URL</label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={surveyUrl}
            readOnly
            className="flex-1 px-3 py-2 text-sm border border-nova-green/30 rounded bg-nova-bg text-white"
          />
          <button
            onClick={handleCopyUrl}
            className="p-2 text-gray-300 hover:text-white hover:bg-nova-green/20 rounded transition-colors"
            title="Copy URL"
          >
            {copied ? <Check className="w-4 h-4 text-nova-green" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2">
        <button
          onClick={handleDownloadQR}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-primary text-white rounded-lg hover:opacity-90 transition-colors"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm">Download</span>
        </button>
        <button
          onClick={handleShare}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-nova-bg border border-nova-green/30 text-white rounded-lg hover:bg-nova-green/20 transition-colors"
        >
          <Share2 className="w-4 h-4" />
          <span className="text-sm">Share</span>
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-xs text-gray-400 text-center">
        Participants can scan this QR code to access the survey
      </div>
    </div>
  );
};

export default SurveyQRCode;


// Example usage in ChatInterface.jsx:
/*
import SurveyQRCode from './SurveyQRCode';

// When displaying AI message with survey creation
{message.toolCalls && message.toolCalls.some(tc => tc.name === 'create_survey') && (
  <div className="mt-4">
    {message.toolCalls
      .filter(tc => tc.name === 'create_survey')
      .map(tc => (
        <SurveyQRCode
          key={tc.id}
          surveyUrl={tc.result?.survey_url || `https://your-app.com/survey/${tc.args.title}`}
          surveyTitle={tc.args.title}
          size={200}
        />
      ))
    }
  </div>
)}
*/
